import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Package } from "lucide-react";
import TopNav from "@/components/TopNav";

const packingChecklist = [
  "Turn OFF the PCB switch.",
  "Turn OFF the LED lights switch.",
  "Remove the micro:bit from the PCB.",
  "Place the game designer kit in the box.",
  "Place the micro:bit in the micro:bit box.",
  "Place the micro:bit box and the USB cable in the zipper inside the top flap of the box.",
  "Place the What's in the Kit card inside the box.",
];

export default function FinalPacking() {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen flex flex-col bg-[var(--bg-base)]"
      data-testid="final-packing-page"
    >
      <TopNav />

      <main className="flex-1 w-full mx-auto max-w-3xl px-4 sm:px-6 pt-8 pb-32">
        <motion.article
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="card-brut bg-white overflow-hidden"
          data-testid="final-packing-card"
        >
          <div className="flex items-start gap-4 p-5 sm:p-6 border-b-[3px] border-[var(--border-heavy)] bg-[var(--primary)]">
            <span
              className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full border-[3px] border-[var(--border-heavy)] bg-white shadow-[3px_3px_0px_var(--border-heavy)]"
              data-testid="final-packing-icon"
            >
              <Package strokeWidth={2.5} className="h-7 w-7 text-[var(--text-main)]" />
            </span>
            <div className="pt-1">
              <p className="font-heading text-xs font-bold uppercase tracking-wider text-[var(--text-main)]/70">
                Game Designer Kit
              </p>
              <h1
                className="mt-0.5 font-heading text-2xl sm:text-3xl font-semibold leading-snug text-[var(--text-main)]"
                data-testid="final-packing-title"
              >
                Testing and Final Packing
              </h1>
            </div>
          </div>

          <div className="p-5 sm:p-6 flex flex-col gap-4">
            <p
              className="text-base sm:text-lg text-[var(--text-main)] leading-relaxed"
              data-testid="final-packing-test-line"
            >
              Test your device by playing the pre-uploaded game.
            </p>
            <p
              className="text-base sm:text-lg text-[var(--text-main)] leading-relaxed"
              data-testid="final-packing-intro"
            >
              Once you flash the game onto the micro:bit, proceed to the final packing.
            </p>
            <p
              className="text-base sm:text-lg font-semibold text-[var(--text-main)] leading-relaxed"
              data-testid="final-packing-subheading"
            >
              Here&apos;s a checklist that will help you with the packing:
            </p>

            <ol
              className="flex flex-col gap-3"
              data-testid="final-packing-substeps"
            >
              {packingChecklist.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3"
                  data-testid={`final-packing-sub-${i + 1}`}
                >
                  <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border-2 border-[var(--border-heavy)] bg-white font-heading text-sm font-bold text-[var(--text-main)]">
                    {i + 1}
                  </span>
                  <p className="text-base sm:text-lg text-[var(--text-main)] leading-relaxed">
                    {item}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </motion.article>
      </main>

      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-6"
        data-testid="final-packing-bottom-nav"
      >
        <button
          type="button"
          onClick={() => navigate("/v/A")}
          aria-label="Back to build flow"
          data-testid="final-packing-prev-btn"
          className="arcade-btn arcade-btn-icon"
        >
          <ChevronLeft strokeWidth={3} className="h-6 w-6" />
        </button>
        <button
          type="button"
          onClick={() => navigate("/team-up")}
          aria-label="Continue"
          data-testid="final-packing-next-btn"
          className="arcade-btn arcade-btn-icon"
        >
          <ChevronRight strokeWidth={3} className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}
